import Chip from "@mui/material/Chip";
import ClickAwayListener from "@mui/material/ClickAwayListener";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import { styled } from "@mui/material";
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CloseIcon from '@mui/icons-material/Close';
import AHDate from "_shared/dates/date";
import { useEffect, useRef, useState } from 'react';

const Timeline = styled('div')(() => ({
  alignItems: 'flex-end',
  backgroundColor: 'rgba(255, 255, 255, 0.01)',
  backdropFilter: "blur(5px)",
  borderLeft: '1px solid rgba(192, 192, 192, 0.25)',
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'nowrap',
  height: '100%',
  minWidth: '50px',
  padding: '64px 4px 16px 8px',
  position: 'absolute',
  top: '50%',
  transform: 'translateY(-50%)',
  right: '20px',
  width: 'auto',
  zIndex: 1,
  opacity: 1,
  // TIMELINE_FADE_MS in use-timeline-idle-fade.ts hides the timeline when this transition ends.
  transition: 'opacity 300ms ease-out',
  "&[hidden]": {
    display: 'none',
  },
  "&[data-fading='true']": {
    opacity: 0,
  },
  "@media (prefers-reduced-motion: reduce)": {
    transition: 'none',
  },
}));
// The column scrolls when its chips do not fit: 24 hour chips are taller than a short chat pane.
const ChipColumn = styled('div')(() => ({
  alignItems: 'flex-end',
  display: 'flex',
  flex: 1,
  flexDirection: 'column',
  gap: '4px',
  marginTop: '8px',
  minHeight: 0,
  overflowY: 'auto',
  padding: '4px 2px',
  scrollbarWidth: 'thin',
}));
const DateChip = styled(Chip)(({ theme }) => ({
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  cursor: 'pointer',
  flexShrink: 0,
  // Auto margins, not justify-content, spread the chips down the column: they fall to zero when
  // the chips overflow, so they never push the first chip above the scroll origin out of reach.
  // Here rather than on the column, because ButtonBase's own margin: 0 outranks a child selector.
  marginBottom: 'auto',
  marginTop: 'auto',
  minWidth: 0,
  maxWidth: 'none',
  textOverflow: 'unset',
  pointerEvents: 'auto',
  whiteSpace: 'nowrap',
  width: 'auto',
  "&:hover, &.Mui-focusVisible": {
    backgroundColor: theme.palette.common.black,
  },
  "&.Mui-focusVisible": {
    outline: `2px solid ${theme.palette.common.white}`,
    outlineOffset: '-2px',
  },
  "&[aria-pressed='true']": {
    backgroundColor: theme.palette.primary.main,
  },
}));
const BackButton = styled(IconButton)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  pointerEvents: 'auto',
  "&:hover": {
    backgroundColor: theme.palette.common.black,
  },
}));
const CloseButton = styled(IconButton)(({ theme }) => ({
  alignSelf: 'center',
  backgroundColor: 'rgba(0, 0, 0, 0.6)',
  color: theme.palette.common.white,
  pointerEvents: 'auto',
  "&:hover": {
    backgroundColor: theme.palette.common.black,
  },
}));

type TimeUnit = 'year' | 'month' | 'week' | 'day' | 'hour';

const Granularity: readonly TimeUnit[] = [
  'year',
  'month',
  'week',
  'day',
  'hour'
];

// A bucket is the span a chip stands for: the dates its label names and the range it filters by.
type Bucket = {
  start: AHDate;
  end: AHDate;
};

// A level is a range and the unit its chips divide it into.
type Level = {
  start: AHDate;
  end: AHDate;
  granularity: TimeUnit;
};

type TimelineProps = {
  className?: string;
  show: boolean;
  /** Fading out, for a timeline that came up by scrolling and was left alone. */
  fading?: boolean;
  /** The pointer moved over the timeline, or off it. */
  onHover?: (hovering: boolean) => void;
  /** The user pressed in the timeline or moved focus into it. */
  onEngaged?: () => void;
  firstDate: AHDate | undefined;
  lastDate: AHDate | undefined;
  onDateSelected: (startDate?: string, endDate?: string) => void;
  onReset: () => void;
}


// Below the top level the range is a chip the user picked, so each bucket is clipped to it: a week in
// September starts no earlier than Sep 1. The top level is left whole, because firstDate and lastDate
// are message dates rather than a range anybody chose.
function computeBuckets(rangeStart: AHDate, rangeEnd: AHDate, unit: TimeUnit, clip: boolean): Bucket[] {
  const buckets: Bucket[] = [];
  let cursor = rangeStart.startOf(unit);
  while (cursor.isSameOrBefore(rangeEnd)) {
    const unitEnd = cursor.endOf(unit);
    buckets.push({
      start: clip && cursor.isBefore(rangeStart) ? rangeStart : cursor,
      end: clip && unitEnd.isAfter(rangeEnd) ? rangeEnd : unitEnd,
    });
    cursor = cursor.add(1, unit);
  }
  return buckets;
}

function formatWeekLabel({ start, end }: Bucket, today: AHDate): string {
  const showYear = start.year !== end.year || end.year !== today.year;
  const year = showYear ? end.format(', YYYY') : '';
  // A month that starts on a Saturday leaves a one-day first week once it is clipped.
  if (start.isSame(end, 'day')) return `${start.format('MMM D')}${year}`;
  const endFormat = start.isSame(end, 'month') ? 'D' : 'MMM D';
  return `${start.format('MMM D')} – ${end.format(endFormat)}${year}`;
}

function formatChipLabel(bucket: Bucket, unit: TimeUnit, today: AHDate): string {
  switch (unit) {
    case 'year': return bucket.start.format('YYYY');
    case 'month': return bucket.start.format('MMM YYYY');
    case 'week': return formatWeekLabel(bucket, today);
    case 'day': return bucket.start.format('ddd, MMM D');
    case 'hour': return bucket.start.format('h A');
  }
}

// The accessible name states the whole range, so a screen reader hears the dates the chip filters by.
function formatChipRange({ start, end }: Bucket, unit: TimeUnit): string {
  if (unit === 'hour') {
    return `${start.format('dddd, MMMM D, YYYY, h:mm A')} – ${end.format('h:mm A')}`;
  }
  if (start.isSame(end, 'day')) return start.format('dddd, MMMM D, YYYY');
  const startFormat = start.year === end.year ? 'dddd, MMMM D' : 'dddd, MMMM D, YYYY';
  return `${start.format(startFormat)} – ${end.format('dddd, MMMM D, YYYY')}`;
}

function pickInitialGranularity(start?: AHDate, end?: AHDate): TimeUnit {
  if (!start || !end) return 'hour';
  if ((end.diff(start, 'years') ?? 0) >= 1) return 'year';
  if ((end.diff(start, 'months') ?? 0) >= 1) return 'month';
  if ((end.diff(start, 'weeks') ?? 0) >= 1) return 'week';
  if ((end.diff(start, 'days') ?? 0) >= 1) return 'day';
  return 'hour';
}

export default function TimelineOverlay(props: Readonly<TimelineProps>) {
  return <TimelineOverlayContent {...props} />;
}

// The content stays mounted while it is hidden, so a scrubber closed with a date filter still applied
// opens again at the level that applied it, with Back still able to undo it. The parent remounts it,
// through its key, when it wants a fresh scrubber.
function TimelineOverlayContent(props: Readonly<TimelineProps>) {
  const {
    className,
    show,
    fading,
    onHover,
    onEngaged,
    firstDate,
    lastDate,
    onDateSelected,
    onReset
  } = props;

  // Only the levels the user opened are state. The top level is read from the props on every render,
  // so a message that arrives while the scrubber is open widens it without losing the levels below:
  // each of those is a chip the user picked, and a new message cannot move a chip's bounds.
  const [openedLevels, setOpenedLevels] = useState<Level[]>([]);
  // The hour is the last level, so an hour chip selects itself rather than opening a level of one.
  const [selectedHour, setSelectedHour] = useState<string | undefined>();

  const chipColumnRef = useRef<HTMLDivElement>(null);
  const focusFirstChip = useRef(false);
  const isDrilledIn = openedLevels.length > 0;
  const level: Level | undefined = openedLevels.at(-1) ?? (firstDate && lastDate
    ? { start: firstDate, end: lastDate, granularity: pickInitialGranularity(firstDate, lastDate) }
    : undefined);
  const granularity = level?.granularity ?? 'hour';

  const dateChips = level ? computeBuckets(level.start, level.end, granularity, isDrilledIn) : [];
  const today = AHDate.TODAY;

  // The chip that had focus is replaced on every level change. Hand focus to the new first chip so a
  // keyboard user keeps their place in the scrubber instead of dropping back to the page. The flag,
  // not the dependency, decides when: this runs after every render, and acts only after a change.
  useEffect(() => {
    if (!focusFirstChip.current) return;
    focusFirstChip.current = false;
    chipColumnRef.current?.querySelector<HTMLElement>('[role="button"]:not([aria-disabled="true"])')?.focus();
  });

  function handleChipClick(bucket: Bucket) {
    if (granularity === 'hour') {
      handleHourClick(bucket);
      return;
    }

    setOpenedLevels([
      ...openedLevels,
      { start: bucket.start, end: bucket.end, granularity: Granularity[Granularity.indexOf(granularity) + 1] },
    ]);
    focusFirstChip.current = true;

    onDateSelected(
      bucket.start.toISOString(),
      bucket.end.toISOString()
    );
  }

  // A second click on the selected hour goes back to the range the hours belong to.
  function handleHourClick(bucket: Bucket) {
    const hour = bucket.start.toISOString();
    if (selectedHour !== hour) {
      setSelectedHour(hour);
      onDateSelected(hour, bucket.end.toISOString());
      return;
    }

    setSelectedHour(undefined);
    if (isDrilledIn) {
      onDateSelected(level!.start.toISOString(), level!.end.toISOString());
    } else {
      onReset();
    }
  }

  // The side effects stay out of any state updater: StrictMode calls an updater twice, which ran the
  // parent's callback twice for one click. Back is drawn only below the top level.
  function handleBackButtonClick() {
    const remaining = openedLevels.slice(0, -1);
    setOpenedLevels(remaining);
    setSelectedHour(undefined);

    const parent = remaining.at(-1);
    if (parent) {
      onDateSelected(parent.start.toISOString(), parent.end.toISOString());
    } else {
      // Close replaces Back at the top level, so the focused button is gone.
      focusFirstChip.current = true;
      onReset();
    }
  }

  return (
    <ClickAwayListener
      mouseEvent={show ? 'onClick' : false}
      touchEvent={show ? 'onTouchEnd' : false}
      onClickAway={() => onDateSelected(undefined, undefined)}
    >
      <Timeline
        className={className}
        hidden={!show}
        data-fading={fading ? 'true' : undefined}
        onPointerEnter={() => onHover?.(true)}
        onPointerLeave={() => onHover?.(false)}
        onPointerDown={onEngaged}
        onFocus={onEngaged}
      >
        {isDrilledIn ? (
          <Tooltip title='Back'>
            <BackButton
              onClick={handleBackButtonClick}
            >
              <ArrowBackIcon />
            </BackButton>
          </Tooltip>
        ) : (
          <Tooltip title='Close'>
            <CloseButton
              onClick={() => onDateSelected(undefined, undefined)}
            >
              <CloseIcon />
            </CloseButton>
          </Tooltip>
        )}
        <ChipColumn ref={chipColumnRef}>
          {dateChips.map((bucket: Bucket) => {
            return (
              <DateChip
                key={bucket.start.toISOString()}
                aria-label={formatChipRange(bucket, granularity)}
                aria-pressed={granularity === 'hour' ? selectedHour === bucket.start.toISOString() : undefined}
                label={formatChipLabel(bucket, granularity, today)}
                size='small'
                onClick={() => handleChipClick(bucket)}
              />
            );
          })}
        </ChipColumn>
      </Timeline>
    </ClickAwayListener>
  );
}
